// ═════════════════════════════════════════════════════════════════════════════
//  LEADS DEL DIAGNÓSTICO · quien tocó la puerta y no entró
//
//  Rob, 24-09-2026, al revisar si el diagnóstico estaba listo para producción.
//  Le conté que cada persona que lo hace y deja su correo se guardaba bien en
//  `onboarding_leads` —con su nota, su nivel real y en qué paso se quedó— y que
//  NADIE podía verlo: el CRM no tenía ninguna pantalla que los mostrara. Se
//  acumulaban y sólo se leían entrando a la base a mano.
//
//  Es la gente más caliente que NSL va a tener: hicieron nueve pasos, vieron su
//  nivel medido y dejaron su contacto sin registrarse.
//
//  ── POR QUÉ NO ES UNA TABLA ─────────────────────────────────────────────────
//  Porque de cincuenta filas, sólo unas pocas piden una decisión hoy: las que
//  tienen permiso de WhatsApp y nadie ha tocado todavía. Una tabla las trata
//  igual que a las demás y las esconde entre cuarenta y cinco iguales. Por eso
//  arriba va el contador de lo que se puede hacer HOY y el filtro llega puesto
//  en «sin tocar».
//
//  ── LA REGLA DEL PERMISO ────────────────────────────────────────────────────
//  El teléfono sólo aparece si esa persona pulsó el botón de confirmación en la
//  pantalla 5.5. Un número guardado sin ese permiso no se enseña, y el servidor
//  ni siquiera lo manda. Que esté en la base no lo convierte en un número al
//  que se pueda escribir.
//
//  ── NO SE ENVÍA NADA DESDE AQUÍ ─────────────────────────────────────────────
//  NSL no tiene pasarela de WhatsApp. El botón abre WhatsApp con el mensaje ya
//  redactado y limpio de emojis (`limpiaParaWhatsApp`, que existe porque los
//  emojis llegaban como «?»), y lo manda gerencia desde su propio número —como
//  el resto del CRM.
// ═════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BadgeCheck, MessageCircle, RefreshCw, Search, Target, UserCheck, Users,
} from 'lucide-react';

import api from '../../../../api/apiClient';
import { enlaceWhatsApp } from '../../../../contacto';
import './LeadsTab.css';

/* ── Los códigos del payload, en palabras ─────────────────────────────────
   El diagnóstico guarda valores en inglés («travel_and_connect») porque son
   los que entiende el resto del sistema. Aquí se leen, así que se traducen. */

const OBJETIVO = {
  lead_meeting_without_hesitation: 'Liderar reuniones',
  travel_and_connect: 'Viajar y conectar',
  academic_certification: 'Título o certificación',
  confidence_and_speak_up: 'Confianza para hablar',
  other_custom: 'Otro (lo escribió)',
};

const NIVEL = {
  zero_absolute: 'Cero absoluto',
  basic: 'Básico (A1-A2)',
  intermediate: 'Intermedio (B1-B2)',
  advanced: 'Avanzado (C1)',
};

const INTENSIDAD = {
  bootcamp: 'Bootcamp',
  marathon: 'Maratón',
  foundations: 'Bases',
  sparring: 'Sparring',
};

const ESTADOS = [
  { id: 'new', texto: 'Sin tocar' },
  { id: 'contacted', texto: 'Contactado' },
  { id: 'customer', texto: 'Ya es cliente' },
  { id: 'discarded', texto: 'Descartado' },
];

const cuando = (iso) => (iso
  ? new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' })
  : '—');

/**
 * El nombre que se usa para saludar, sacado del correo.
 *
 * Sólo la PRIMERA palabra: «ana.martinez@…» da «Ana», no «Ana martinez». Un
 * apellido adivinado de un correo se equivoca a menudo, y equivocarse en el
 * nombre es la forma más rápida de que el mensaje parezca automático — que es
 * justo lo contrario de lo que se le prometió a esa persona.
 */
const nombreDeCorreo = (correo) => {
  const antes = String(correo || '').split('@')[0] || '';
  const primera = antes.split(/[._\-0-9]+/).filter(Boolean)[0] || '';
  // Dos letras o menos no es un nombre: es una inicial o ruido.
  if (primera.length < 3) return '';
  return primera.charAt(0).toUpperCase() + primera.slice(1).toLowerCase();
};

/**
 * El mensaje que gerencia va a mandar. Se escribe aquí y no en el servidor
 * porque es copy, no dato: cambiarlo no debería exigir un despliegue del
 * backend. Sin emojis a propósito — ver `limpiaParaWhatsApp`.
 */
function mensajeDe(lead) {
  const nombre = nombreDeCorreo(lead.contact_email);
  const saludo = nombre ? `Hola ${nombre}` : 'Hola';
  const nivel = lead.estimated_cefr ? ` Tu nivel medido salió *${lead.estimated_cefr}*.` : '';
  const meta = OBJETIVO[lead.aspiration]
    ? ` Vi que tu objetivo es *${OBJETIVO[lead.aspiration].toLowerCase()}*.`
    : '';
  return (
    `${saludo}, te escribo de *Non Stop Learning*. `
    + `Hiciste el diagnóstico en nuestra página y me pediste que te mandara el resultado.`
    + `${nivel}${meta}\n\n`
    + `Tu plan de entrenamiento ya está listo. ¿Te lo paso por aquí o prefieres que te llame?`
  );
}

export default function LeadsTab({ t, dark = true }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [buscar, setBuscar] = useState('');
  // Llega puesto en «sin tocar»: es lo único de esta pantalla que pide una
  // decisión hoy. Ver el comentario de cabecera.
  const [estado, setEstado] = useState('new');
  const [soloPermiso, setSoloPermiso] = useState(false);
  const [guardando, setGuardando] = useState(null);

  const tk = t || {
    bg: dark ? '#1a1a1a' : '#e0e5ec',
    tx: dark ? '#e8e8f0' : '#1a1a2e',
    tx2: dark ? '#7a7a9a' : '#7a8499',
    ac: dark ? '#00FFFF' : '#ffff00',
  };

  const carga = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const { data } = await api.get('/api/admin/leads', {
        params: {
          buscar: buscar.trim() || undefined,
          estado: estado || undefined,
          solo_con_permiso: soloPermiso || undefined,
          limite: 50,
        },
      });
      setDatos(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar la lista de leads.');
    } finally {
      setCargando(false);
    }
  }, [buscar, estado, soloPermiso]);

  useEffect(() => { carga(); }, [estado, soloPermiso]);   // eslint-disable-line react-hooks/exhaustive-deps

  const cambiaEstado = async (lead, nuevo) => {
    setGuardando(lead.id);
    try {
      await api.patch(`/api/admin/leads/${lead.id}`, { status: nuevo });
      await carga();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar el cambio.');
    } finally {
      setGuardando(null);
    }
  };

  const resumen = datos?.resumen;
  const leads = datos?.leads || [];

  const cifras = useMemo(() => ([
    { id: 'listos', icono: MessageCircle, valor: resumen?.con_permiso_whatsapp ?? 0,
      texto: 'con permiso de WhatsApp', destacada: true },
    { id: 'sin', icono: Target, valor: resumen?.sin_tocar ?? 0, texto: 'sin tocar' },
    { id: 'fin', icono: UserCheck, valor: resumen?.completaron ?? 0, texto: 'vieron su plan' },
    { id: 'total', icono: Users, valor: resumen?.total ?? 0, texto: 'en esta vista' },
  ]), [resumen]);

  return (
    <div className="leads" style={{ color: tk.tx }}>
      <header className="leads-cabecera">
        <div>
          <h2 className="leads-titulo">Leads del diagnóstico</h2>
          <p className="leads-bajada" style={{ color: tk.tx2 }}>
            Hicieron el diagnóstico completo y dejaron su contacto sin crear cuenta.
          </p>
        </div>
        <button type="button" className="leads-recarga" onClick={carga} disabled={cargando}>
          <RefreshCw size={15} className={cargando ? 'leads-girando' : ''} />
          Actualizar
        </button>
      </header>

      <div className="leads-cifras">
        {cifras.map(({ id, icono: Icono, valor, texto, destacada }) => (
          <div key={id} className={`leads-cifra${destacada ? ' destacada' : ''}`}>
            <Icono size={17} />
            <strong>{valor.toLocaleString('es-MX')}</strong>
            <span>{texto}</span>
          </div>
        ))}
      </div>

      <div className="leads-filtros">
        <div className="leads-buscador">
          <Search size={15} />
          <input
            type="search"
            placeholder="Buscar por correo…"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') carga(); }}
          />
        </div>

        <div className="leads-pestanas">
          <button type="button" className={estado === '' ? 'activa' : ''}
                  onClick={() => setEstado('')}>Todos</button>
          {ESTADOS.map((e) => (
            <button key={e.id} type="button" className={estado === e.id ? 'activa' : ''}
                    onClick={() => setEstado(e.id)}>{e.texto}</button>
          ))}
        </div>

        <label className="leads-interruptor">
          <input type="checkbox" checked={soloPermiso}
                 onChange={(e) => setSoloPermiso(e.target.checked)} />
          Solo con permiso de WhatsApp
        </label>
      </div>

      {error && <p className="leads-error">{error}</p>}

      {cargando && <p className="leads-vacio">Cargando…</p>}

      {!cargando && leads.length === 0 && (
        <p className="leads-vacio">
          Nadie por aquí todavía con ese filtro. Los leads aparecen cuando
          alguien termina el diagnóstico y deja su correo.
        </p>
      )}

      <ul className="leads-lista">
        {leads.map((lead) => (
          <li key={lead.id} className="leads-fila">
            <div className="leads-quien">
              <span className="leads-correo">{lead.contact_email || '(sin correo)'}</span>
              <span className="leads-cuando">{cuando(lead.created_at)}</span>
              {lead.ya_es_alumno && (
                <span className="leads-chapa alumno">
                  <BadgeCheck size={12} /> ya es alumno
                </span>
              )}
              {lead.whatsapp_ok && (
                <span className="leads-chapa permiso">permiso de WhatsApp</span>
              )}
            </div>

            <div className="leads-que">
              {lead.estimated_cefr && (
                <span className="leads-nivel">
                  {lead.estimated_cefr}
                  {lead.technical_score !== null && lead.technical_score !== undefined
                    && ` · ${lead.technical_score}/10`}
                </span>
              )}
              <span>{OBJETIVO[lead.aspiration] || '—'}</span>
              <span>{NIVEL[lead.perceived_level] || '—'}</span>
              <span>{INTENSIDAD[lead.training_intensity] || '—'}</span>
              {lead.funnel_stage_reached === 'completed_no_signup' && (
                <span className="leads-completo">vio su plan</span>
              )}
            </div>

            <div className="leads-acciones">
              {lead.whatsapp_ok && lead.contact_phone ? (
                <a
                  className="leads-wa"
                  href={enlaceWhatsApp(mensajeDe(lead), lead.contact_phone.replace('+', ''))}
                  target="_blank"
                  rel="noreferrer"
                  title={lead.contact_phone}
                >
                  <MessageCircle size={14} /> Escribir
                </a>
              ) : (
                <span className="leads-sin-permiso" title="No pulsó el botón de confirmación">
                  sin permiso
                </span>
              )}

              <select
                className="leads-estado"
                value={lead.status}
                disabled={guardando === lead.id}
                onChange={(e) => cambiaEstado(lead, e.target.value)}
              >
                {ESTADOS.map((e) => (
                  <option key={e.id} value={e.id}>{e.texto}</option>
                ))}
              </select>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
